import { trackContactPageEvent } from "../../common/events";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll, inViewTrigger } from "../../common/html";
import { getCookie } from "../../utils/cookie";
import { compositeDisposable } from "../../utils/disposable";
import createFileUpload, { IGetSignedUrl } from "../molecules/file-upload";
import { initForm } from "../molecules/form";
import { createTruncate } from "./truncate";
import { isTempAccountEmail } from "../../utils/validation";
import { getParameter } from "../../utils/querystring";

export interface ITopicWithSubtopicsResult {
	informativeTitle: string;
	informativeText: string;
	subtopics: string[];
}

export interface IContactFormSubmitResult {
	error: string;
	text: string;
	title: string;
}

export interface IContactFormDependencies {
	getTopicWithSubtopics: (index: number) => Promise<ITopicWithSubtopicsResult>;
	submitData: (data: { [inputName: string]: string }) => Promise<IContactFormSubmitResult>;
	showInformation: (title: string, label: string) => void;
	getUploadUrl: (sessionId: string, filename: string) => Promise<IGetSignedUrl>;
	getPreventedFileTypes: () => Promise<string>;
}

export default function createContactForm(
	containerEl: HTMLElement,
	formEl: HTMLElement,
	thankYouEl: HTMLElement,
	deps: IContactFormDependencies,
) {
	const { getTopicWithSubtopics, submitData, getUploadUrl, getPreventedFileTypes, showInformation } = deps;

	const topicDropdown = expressQuerySelector<HTMLSelectElement>(formEl, ".technical-contact-form-topic", true);
	const subtopicDropdown = expressQuerySelector<HTMLSelectElement>(formEl, ".technical-contact-form-subtopic", true);
	const suggestionCloseBt = expressQuerySelector<HTMLSelectElement>(formEl, ".technical-suggestion-close", false);
	const submitBt = expressQuerySelector<HTMLButtonElement>(formEl, ".technical-contact-form-submit", true);
	const thankYouTitleEl = expressQuerySelector<HTMLElement>(thankYouEl, ".technical-contact-thankyou-title", true);
	const thankYouTextEl = expressQuerySelector<HTMLElement>(thankYouEl, ".technical-contact-thankyou-description", true);
	const suggestion = expressQuerySelector<HTMLSelectElement>(formEl, ".technical-suggestion", true);
	const numberOfLines = 4;
	const initializedTruncateEls = new Set<HTMLElement>();

	const changeTopic = () => {
		trackContactPageEvent("TopicSelected");
		if (topicDropdown.value)
			getTopicWithSubtopics(parseInt(topicDropdown.value, 10)).then((res) => {
				subtopicDropdown.disabled = false;
				subtopicDropdown.closest(".a-form-input").classList.remove("a-form-input--disabled");

				// Remove all existing options (except the first one)
				for (let i = subtopicDropdown.options.length - 1; i >= 1; i--) subtopicDropdown.remove(i);
				subtopicDropdown.selectedIndex = 0;

				suggestion.classList.add("u-hide"); // suggestion will be shown when a subtopic is selected

				let counter = 0;
				res.subtopics.forEach((s) => {
					const newSubtopic = document.createElement("option");
					newSubtopic.text = s;
					newSubtopic.value = counter.toString();
					subtopicDropdown.add(newSubtopic);
					counter++;
				});
			});
	};

	const changeSubtopic = () => {
		trackContactPageEvent("SubtopicSelected");
		if (!subtopicDropdown.value) return;

		// reset all to invisible
		const faqItems = expressQuerySelectorAll(suggestion, ".technical-faq-item:not(.u-hide)");
		faqItems.forEach((x) => x.classList.add("u-hide"));

		// search specific item
		const infoItem = expressQuerySelector<HTMLElement>(
			suggestion,
			`[data-topicIndex="${parseInt(topicDropdown.value, 10)}"][data-subTopicIndex="${parseInt(subtopicDropdown.value, 10)}"]`,
			false,
		);
		if (!infoItem) return;

		// show info item
		infoItem.classList.remove("u-hide");
		suggestion.classList.remove("u-hide");

		initTruncate(infoItem);

		// show or hide the order number
		const orderNr = expressQuerySelector<HTMLElement>(formEl, ".technical-contact-ordernumber", true);
		orderNr && orderNr.classList.toggle("u-hide", infoItem.dataset.ordernrrequired !== "true");
	};

	const initTruncate = (faqItemEl: HTMLElement) => {
		if (initializedTruncateEls.has(faqItemEl)) return;
		initializedTruncateEls.add(faqItemEl);

		const shaveControlsContainer = expressQuerySelector(faqItemEl, ".technical-shave-controls", true);
		const suggestionText = expressQuerySelector<HTMLElement>(faqItemEl, ".technical-suggestion-text", true);
		const lineHeight = parseFloat(window.getComputedStyle(suggestionText).lineHeight || "0");
		const maxHeight = numberOfLines * lineHeight;

		createTruncate(suggestionText, {
			closeEventAction: "ShowLessInformativeText",
			openEventAction: "ShowMoreInformativeText",
			eventTrackingMethod: (action: string) => trackContactPageEvent(action),
			lines: numberOfLines,
			maxHeight: maxHeight,
			lineHeight: lineHeight,
			showMore:
				shaveControlsContainer &&
				expressQuerySelector<HTMLElement>(shaveControlsContainer, ".technical-shave-more", true).outerHTML,
			showLess:
				shaveControlsContainer &&
				expressQuerySelector<HTMLElement>(shaveControlsContainer, ".technical-shave-less", true).outerHTML,
			onCloseEvent: () => formEl.scrollIntoView(),
		});
	};

	const closeSuggestion = (e: MouseEvent) => {
		trackContactPageEvent("InformativeTextClosed");
		const parentEl = (e.currentTarget as HTMLElement).parentElement;
		if (parentEl) parentEl.classList.add("u-hide");
	};

	const resetSelectedOptions = () => {
		// reset the default selected values of the selects (for FF and older browser that don't support the autocomplete attribute)
		topicDropdown.options.selectedIndex = 0;
		subtopicDropdown.options.selectedIndex = 0;
	};

	const eventDisposables = compositeDisposable([
		expressEventListener(topicDropdown, "change", changeTopic),
		expressEventListener(subtopicDropdown, "change", changeSubtopic),
		suggestionCloseBt && expressEventListener(suggestionCloseBt, "click", closeSuggestion),
		expressEventListener(window, "onbeforeunload", resetSelectedOptions),
	]);

	const showThankYou = (title: string, text: string) => {
		setTimeout(() => {
			containerEl.classList.toggle("u-hide", true);
			thankYouTitleEl.innerHTML = title;
			thankYouTextEl.innerHTML = text;
			thankYouEl.classList.toggle("u-hide", false);
			thankYouEl.scrollIntoView(true);
		}, 401);
	};

	// Add event tracking
	inViewTrigger(containerEl, () => {
		trackContactPageEvent("ContactFormRead");
	});

	// Enable the actual form to submit data
	const contactForm = initForm(formEl as HTMLFormElement, {
		onSubmitForm: (data) => {
			data["files"] = fileUpload.GetFileUrls();
			return submitData(data).then((res) => {
				if (res.error) {
					switch (res.error) {
						case "ordernr":
							contactForm.inputs["contactordernr"].setError(res.text);
							break;
						case "spam":
							showThankYou(res.title, res.text);
							break;
						default:
							break;
					}
				} else {
					showThankYou(res.title, res.text);
				}
			});
		},
		submitOnEnter: true,
	});
	const toggleEnableDisableButton = () => {
		if (fileUpload.IsUploadComplete()) {
			submitBt.classList.remove("a-button--disabled");
			submitBt.disabled = false;
		} else if (!submitBt.disabled) {
			submitBt.classList.add("a-button--disabled");
			submitBt.disabled = true;
		}
	};
	const onUploadFile = () => {
		contactForm.inputs["uploadfield"].clear(false);
		toggleEnableDisableButton();
	};

	const fileUpload = createFileUpload(containerEl, {
		getPreventedFileTypes: getPreventedFileTypes,
		getUploadUrl: getUploadUrl,
		onUpload: onUploadFile,
		onUploadComplete: toggleEnableDisableButton,
		onUploadFailed: toggleEnableDisableButton,
		onUploadLoad: toggleEnableDisableButton,
		showInformation: showInformation,
		onFilesSelected: toggleEnableDisableButton,
		onFileDeleted: toggleEnableDisableButton,
	});

	// Wha?
	// It seems that BACK-FORWARD in the browser causes the topic to be preselected but the subtopic to still be disabled. Solution, reset the topic.
	// only do this when nothing is preselected
	!getParameter("subtopic") && contactForm.inputs["topic"].clear(true);

	const email = getCookie("LastUsedEmail");
	email && !isTempAccountEmail(email) && contactForm.inputs["contactemail"].setValue(email);

	const shownSuggestion = expressQuerySelector<HTMLElement>(suggestion, ".technical-faq-item:not(.u-hide)", false);
	if (shownSuggestion) {
		initTruncate(shownSuggestion);
		formEl.scrollIntoView();
	}

	return {
		dispose: () => {
			eventDisposables.dispose();
			fileUpload.dispose();
		},
	};
}
